/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/prop-types */

import { useEffect, useCallback, useMemo, useState } from "react";
import {
  Box,
  TextField,
  Checkbox,
  Typography,
  Card,
  CardContent,
  Paper,
  Fade,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import Select from "react-select";
import { useTheme } from "@mui/material/styles";
import { getSingleSelectStyles, getMultiSelectStyles } from "@/lib/styles";
import FileUploadField from "@/components/FileUploadField";
import { useTranslation } from "react-i18next";

const StepInputsForm = ({
  setFileInputValues,
  fileInputValues,
  setInputValues,
  inputValues,
  stepInputs = [],
  onChange,
  userList = [],
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [touched, setTouched] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const singleSelectStyles = useMemo(
    () => getSingleSelectStyles(theme),
    [theme]
  );
  const multiSelectStyles = useMemo(() => getMultiSelectStyles(theme), [theme]);

  useEffect(() => {
    if (!stepInputs.length) return;
    if (Object.keys(inputValues).length > 0) return;

    const initialValues = stepInputs.reduce((acc, input) => {
      const type = (input.input_type || "").toUpperCase();
      let value;

      switch (type) {
        case "BOOLEAN":
          // Boolean: Use default_value if provided, otherwise "NO" (false)
          if (Object.hasOwn(input, "default_value") && input.default_value) {
            const defaultVal = String(input.default_value).toUpperCase();
            if (
              defaultVal === "YES" ||
              defaultVal === "TRUE" ||
              defaultVal === "1"
            ) {
              value = "YES";
            } else {
              value = "NO"; // Default to NO for any other value
            }
          } else {
            value = "NO"; // No default value - start with NO
          }
          break;
        case "CONFIRMATION":
          // Confirmation always starts with false (unchecked)
          value = false;
          break;
        case "MULTI_USER_SELECTION":
        case "MULTI_SELECTION":
          value = Object.hasOwn(input, "default_value")
            ? Array.isArray(input.default_value)
              ? input.default_value
              : input.default_value
                ? input.default_value.split(",").filter(Boolean)
                : []
            : [];
          break;
        case "SELECTION":
        case "USER_SELECTION":
          value = Object.hasOwn(input, "default_value")
            ? input.default_value
            : "";
          break;
        default:
          value = Object.hasOwn(input, "default_value")
            ? input.default_value
            : "";
          break;
      }

      acc[input.id] = value;
      return acc;
    }, {});

    const hasChanges = Object.entries(initialValues).some(
      ([key, val]) => inputValues[key] !== val
    );

    if (hasChanges) {
      setInputValues((prev) => ({
        ...prev,
        ...initialValues,
      }));
    }
  }, [stepInputs]);

  const handleInputChange = useCallback(
    (id, value) => {
      setInputValues((prev) => ({
        ...prev,
        [id]: value,
      }));
      onChange(id, value);
    },
    [onChange, setInputValues]
  );

  const handleFileInputChange = useCallback(
    (id, files) => {
      const fileArray = Array.from(files);
      setFileInputValues((prev) => ({
        ...prev,
        [id]: fileArray,
      }));
      onChange(id, fileArray);
    },
    [setFileInputValues, onChange]
  );

  const handleBlur = useCallback((id) => {
    setTouched((prev) => ({ ...prev, [id]: true }));
  }, []);

  const validateField = useCallback(
    (input, value, files) => {
      const type = (input.input_type || "").toUpperCase();

      // CONFIRMATION is always required (must be checked)
      if (type === "CONFIRMATION") {
        return value === true
          ? ""
          : t("stepInputsForm.errorConfirmation", {
              field: input.name,
              defaultValue: `${input.name} must be confirmed`,
            });
      }

      // For other types, check if field is required
      if (!input.required) return "";

      switch (type) {
        case "BOOLEAN":
          return value === "YES" || value === "NO"
            ? ""
            : t("stepInputsForm.errorBoolean", {
                field: input.name,
                defaultValue: `${input.name} must be selected`,
              });
        case "MULTI_USER_SELECTION":
        case "MULTI_SELECTION":
          return Array.isArray(value) && value.length > 0
            ? ""
            : t("stepInputsForm.errorMultiSelect", {
                field: input.name,
                defaultValue: `Select at least one ${input.name}`,
              });
        case "FILE_UPLOAD":
          return Array.isArray(files) && files.length > 0
            ? ""
            : t("stepInputsForm.errorFileUpload", {
                field: input.name,
                defaultValue: `Upload at least one file for ${input.name}`,
              });
        case "SHORT_TEXT":
        case "TEXT":
        case "NUMERIC":
        case "TIMESTAMP":
        case "SELECTION":
        case "USER_SELECTION":
          return value && String(value).trim() !== ""
            ? ""
            : t("stepInputsForm.errorRequired", {
                field: input.name,
                defaultValue: `${input.name} is required`,
              });
        default:
          return "";
      }
    },
    [t]
  );

  // Update validation errors
  useEffect(() => {
    const errors = stepInputs.reduce((acc, input) => {
      const type = (input.input_type || "").toUpperCase();
      // Always validate CONFIRMATION types, or validate if field is required
      if (type === "CONFIRMATION" || input.required) {
        const error = validateField(
          input,
          inputValues[input.id],
          fileInputValues[input.id]
        );
        if (error) acc[input.id] = error;
      }
      return acc;
    }, {});
    setValidationErrors(errors);
  }, [inputValues, fileInputValues, stepInputs, validateField]);

  const getSelectOptions = useCallback((options) => {
    if (!Array.isArray(options)) {
      return [];
    }
    return options.map((option) => ({
      value: option,
      label: option,
    }));
  }, []);

  const renderField = useCallback(
    (input, index) => {
      const type = (input.input_type || "").toUpperCase();
      const required = input.required || false;
      const error = touched[input.id] && validationErrors[input.id];
      // CONFIRMATION is always required, or check if field is required
      const isValid =
        touched[input.id] &&
        !validationErrors[input.id] &&
        (type === "CONFIRMATION" || input.required);

      const fieldWrapper = (content) => (
        <Fade in timeout={300 + index * 100} key={input.id}>
          <Card
            sx={{
              mb: 2,
              border: `1px solid ${error ? theme.palette.error.main : theme.palette.checklist.border.subtle}`,
              bgcolor: theme.palette.checklist.surface.elevated,
              borderRadius: 2,
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                borderColor: error
                  ? theme.palette.error.main
                  : theme.palette.checklist.border.focus,
                boxShadow: theme.shadows[1],
              },
            }}
          >
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {input.name}
                  {required && (
                    <span style={{ color: theme.palette.error.main }}> *</span>
                  )}
                </Typography>
                {isValid && (
                  <CheckCircleIcon
                    sx={{
                      ml: 1,
                      fontSize: 16,
                      color: theme.palette.success.main,
                    }}
                  />
                )}
                {error && (
                  <WarningIcon
                    sx={{
                      ml: 1,
                      fontSize: 16,
                      color: theme.palette.error.main,
                    }}
                  />
                )}
                {input.description && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ ml: 1, fontStyle: "italic" }}
                  >
                    ({input.description})
                  </Typography>
                )}
              </Box>
              {content}
              {error && (
                <Typography
                  variant="caption"
                  sx={{ mt: 1, color: "error.main", display: "block" }}
                >
                  {error}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Fade>
      );

      switch (type) {
        case "SHORT_TEXT":
        case "TEXT":
          return fieldWrapper(
            <TextField
              fullWidth
              multiline
              rows={input.input_type === "TEXT" ? 3 : 1}
              value={inputValues[input.id] || ""}
              onChange={(e) => handleInputChange(input.id, e.target.value)}
              onBlur={() => handleBlur(input.id)}
              variant="outlined"
              size="small"
              required={required}
              error={!!error}
              placeholder={t("stepInputsForm.textFieldPlaceholder")}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: theme.palette.checklist.surface.primary,
                },
              }}
            />
          );
        case "NUMERIC":
          return fieldWrapper(
            <TextField
              fullWidth
              type="number"
              value={inputValues[input.id] || ""}
              onChange={(e) => handleInputChange(input.id, e.target.value)}
              onBlur={() => handleBlur(input.id)}
              variant="outlined"
              size="small"
              required={required}
              error={!!error}
              placeholder={t("stepInputsForm.numericFieldPlaceholder")}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: theme.palette.checklist.surface.primary,
                },
              }}
            />
          );
        case "BOOLEAN":
          return (
            <Fade in timeout={300 + index * 100} key={input.id}>
              <Card
                sx={{
                  mb: 2,
                  border: `1px solid ${error ? theme.palette.error.main : theme.palette.checklist.border.subtle}`,
                  bgcolor: theme.palette.checklist.surface.elevated,
                  borderRadius: 2,
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    borderColor: error
                      ? theme.palette.error.main
                      : theme.palette.checklist.border.focus,
                    boxShadow: theme.shadows[1],
                  },
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", flex: 1 }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {input.name}
                        {required && (
                          <span style={{ color: theme.palette.error.main }}>
                            {" "}
                            *
                          </span>
                        )}
                      </Typography>
                      {isValid && (
                        <CheckCircleIcon
                          sx={{
                            ml: 1,
                            fontSize: 16,
                            color: theme.palette.success.main,
                          }}
                        />
                      )}
                      {error && (
                        <WarningIcon
                          sx={{
                            ml: 1,
                            fontSize: 16,
                            color: theme.palette.error.main,
                          }}
                        />
                      )}
                    </Box>

                    <ToggleButtonGroup
                      value={inputValues[input.id] || null}
                      exclusive
                      onChange={(e, newValue) => {
                        handleInputChange(input.id, newValue);
                      }}
                      onBlur={() => handleBlur(input.id)}
                      sx={{ gap: 0.5 }}
                    >
                      <ToggleButton
                        value="YES"
                        aria-label="yes"
                        sx={{
                          px: 2,
                          py: 0.5,
                          borderRadius: 3,
                          fontWeight: 500,
                          textTransform: "none",
                          minWidth: 60,
                          fontSize: "0.8125rem",
                          transition: "all 0.3s ease",
                          border: `1px solid ${theme.palette.checklist.border.subtle}`,
                          bgcolor:
                            inputValues[input.id] === "YES"
                              ? theme.palette.success.dark
                              : theme.palette.checklist.surface.elevated,
                          color:
                            inputValues[input.id] === "YES"
                              ? theme.palette.common.white
                              : theme.palette.text.primary,
                          "&:hover": {
                            bgcolor: theme.palette.success.main,
                            color: theme.palette.common.white,
                          },
                          "&.Mui-selected": {
                            bgcolor: theme.palette.success.dark,
                            color: theme.palette.common.white,
                            boxShadow: theme.shadows[2],
                            transform: "scale(1.05)",
                            borderColor: theme.palette.checklist.border.focus,
                            "&:hover": {
                              bgcolor: theme.palette.success.main,
                            },
                          },
                        }}
                      >
                        {t("stepInputsForm.yes", "YES")}
                      </ToggleButton>
                      <ToggleButton
                        value="NO"
                        aria-label="no"
                        sx={{
                          px: 2,
                          py: 0.5,
                          borderRadius: 3,
                          fontWeight: 500,
                          textTransform: "none",
                          minWidth: 60,
                          fontSize: "0.8125rem",
                          transition: "all 0.3s ease",
                          border: `1px solid ${theme.palette.checklist.border.subtle}`,
                          bgcolor:
                            inputValues[input.id] === "NO"
                              ? theme.palette.error.dark
                              : theme.palette.checklist.surface.elevated,
                          color:
                            inputValues[input.id] === "NO"
                              ? theme.palette.common.white
                              : theme.palette.text.primary,
                          "&:hover": {
                            bgcolor: theme.palette.error.main,
                            color: theme.palette.common.white,
                          },
                          "&.Mui-selected": {
                            bgcolor: theme.palette.error.dark,
                            color: theme.palette.common.white,
                            boxShadow: theme.shadows[2],
                            transform: "scale(1.05)",
                            borderColor: theme.palette.checklist.border.focus,
                            "&:hover": {
                              bgcolor: theme.palette.error.main,
                            },
                          },
                        }}
                      >
                        {t("stepInputsForm.no", "NO")}
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Box>

                  {input.description && (
                    <Typography
                      variant="body2"
                      sx={{ mt: 1, color: "text.secondary" }}
                    >
                      {input.description}
                    </Typography>
                  )}

                  {error && (
                    <Typography
                      variant="caption"
                      sx={{ mt: 1, color: "error.main", display: "block" }}
                    >
                      {error}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Fade>
          );
        case "CONFIRMATION":
          return (
            <Fade in timeout={300 + index * 100} key={input.id}>
              <Card
                sx={{
                  mb: 2,
                  border: `1px solid ${error ? theme.palette.error.main : theme.palette.checklist.border.subtle}`,
                  bgcolor: theme.palette.checklist.surface.elevated,
                  borderRadius: 2,
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    borderColor: error
                      ? theme.palette.error.main
                      : theme.palette.checklist.border.focus,
                    boxShadow: theme.shadows[1],
                  },
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", flex: 1 }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {input.name}
                        <span style={{ color: theme.palette.error.main }}>
                          {" "}
                          * {/* Always required */}
                        </span>
                      </Typography>
                      {isValid && (
                        <CheckCircleIcon
                          sx={{
                            ml: 1,
                            fontSize: 16,
                            color: theme.palette.success.main,
                          }}
                        />
                      )}
                      {error && (
                        <WarningIcon
                          sx={{
                            ml: 1,
                            fontSize: 16,
                            color: theme.palette.error.main,
                          }}
                        />
                      )}
                    </Box>

                    <Checkbox
                      checked={Boolean(inputValues[input.id])}
                      onChange={(e) =>
                        handleInputChange(input.id, e.target.checked)
                      }
                      onBlur={() => handleBlur(input.id)}
                      sx={{
                        "&.Mui-checked": {
                          color: theme.palette.primary.main,
                        },
                      }}
                    />
                  </Box>

                  {input.description && (
                    <Typography
                      variant="body2"
                      sx={{ mt: 1, color: "text.secondary" }}
                    >
                      {input.description}
                    </Typography>
                  )}

                  {error && (
                    <Typography
                      variant="caption"
                      sx={{ mt: 1, color: "error.main", display: "block" }}
                    >
                      {error}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Fade>
          );
        case "TIMESTAMP":
          return fieldWrapper(
            <TextField
              fullWidth
              type="datetime-local"
              value={inputValues[input.id] || ""}
              onChange={(e) => handleInputChange(input.id, e.target.value)}
              onBlur={() => handleBlur(input.id)}
              variant="outlined"
              size="small"
              required={required}
              error={!!error}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: theme.palette.checklist.surface.primary,
                },
              }}
            />
          );
        case "USER_SELECTION":
          return fieldWrapper(
            <Select
              styles={{
                ...singleSelectStyles,
                menuPortal: (base) => ({ ...base, zIndex: 1301 }),
                control: (base, state) => ({
                  ...base,
                  backgroundColor: theme.palette.checklist.surface.primary,
                  borderColor: error
                    ? theme.palette.error.main
                    : state.isFocused
                      ? theme.palette.primary.main
                      : theme.palette.checklist.border.subtle,
                }),
              }}
              options={userList}
              value={
                userList.find((opt) => opt.value === inputValues[input.id]) ||
                null
              }
              onChange={(selected) =>
                handleInputChange(input.id, selected ? selected.value : "")
              }
              onBlur={() => handleBlur(input.id)}
              placeholder={t("stepInputsForm.selectUserPlaceholder")}
              isClearable={true}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              required={required}
            />
          );
        case "MULTI_USER_SELECTION":
          return fieldWrapper(
            <Select
              isMulti
              styles={{
                ...multiSelectStyles,
                menuPortal: (base) => ({ ...base, zIndex: 1301 }),
                control: (base, state) => ({
                  ...base,
                  backgroundColor: theme.palette.checklist.surface.primary,
                  borderColor: error
                    ? theme.palette.error.main
                    : state.isFocused
                      ? theme.palette.primary.main
                      : theme.palette.checklist.border.subtle,
                }),
              }}
              closeMenuOnSelect={false}
              options={userList}
              value={
                Array.isArray(inputValues[input.id])
                  ? inputValues[input.id]
                      .map((val) => userList.find((opt) => opt.value === val))
                      .filter(Boolean)
                  : []
              }
              onChange={(selectedOptions) =>
                handleInputChange(
                  input.id,
                  selectedOptions ? selectedOptions.map((opt) => opt.value) : []
                )
              }
              onBlur={() => handleBlur(input.id)}
              placeholder={t("stepInputsForm.selectUsersPlaceholder")}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              required={required}
            />
          );
        case "SELECTION": {
          const options = getSelectOptions(input.options);
          return fieldWrapper(
            <>
              <Select
                styles={{
                  ...singleSelectStyles,
                  menuPortal: (base) => ({ ...base, zIndex: 1301 }),
                  control: (base, state) => ({
                    ...base,
                    backgroundColor: theme.palette.checklist.surface.primary,
                    borderColor: error
                      ? theme.palette.error.main
                      : state.isFocused
                        ? theme.palette.primary.main
                        : theme.palette.checklist.border.subtle,
                  }),
                }}
                options={options}
                value={
                  options.find((opt) => opt.value === inputValues[input.id]) ||
                  null
                }
                onChange={(selected) =>
                  handleInputChange(input.id, selected ? selected.value : "")
                }
                onBlur={() => handleBlur(input.id)}
                placeholder={t("stepInputsForm.selectOptionPlaceholder")}
                isClearable={true}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                isDisabled={!options.length}
                required={required}
              />
              {!options.length && (
                <Typography
                  color="error"
                  variant="caption"
                  sx={{ mt: 1, display: "block" }}
                >
                  {t("stepInputsForm.noOptions", { field: input.name })}
                </Typography>
              )}
            </>
          );
        }
        case "MULTI_SELECTION": {
          const multiOptions = getSelectOptions(input.options);
          return fieldWrapper(
            <>
              <Select
                isMulti
                styles={{
                  ...multiSelectStyles,
                  menuPortal: (base) => ({ ...base, zIndex: 1301 }),
                  control: (base, state) => ({
                    ...base,
                    backgroundColor: theme.palette.checklist.surface.primary,
                    borderColor: error
                      ? theme.palette.error.main
                      : state.isFocused
                        ? theme.palette.primary.main
                        : theme.palette.checklist.border.subtle,
                  }),
                }}
                closeMenuOnSelect={false}
                options={multiOptions}
                value={
                  Array.isArray(inputValues[input.id])
                    ? inputValues[input.id]
                        .map((val) =>
                          multiOptions.find((opt) => opt.value === val)
                        )
                        .filter(Boolean)
                    : []
                }
                onChange={(selectedOptions) =>
                  handleInputChange(
                    input.id,
                    selectedOptions
                      ? selectedOptions.map((opt) => opt.value)
                      : []
                  )
                }
                onBlur={() => handleBlur(input.id)}
                placeholder={t("stepInputsForm.selectOptionsPlaceholder")}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                isDisabled={!multiOptions.length}
                required={required}
              />
              {!multiOptions.length && (
                <Typography
                  color="error"
                  variant="caption"
                  sx={{ mt: 1, display: "block" }}
                >
                  {t("stepInputsForm.noOptions", { field: input.name })}
                </Typography>
              )}
            </>
          );
        }
        case "FILE_UPLOAD":
          return fieldWrapper(
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: theme.palette.checklist.surface.primary,
                border: `2px dashed ${theme.palette.checklist.border.subtle}`,
                borderRadius: 1,
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: theme.palette.checklist.border.focus,
                },
              }}
            >
              <FileUploadField
                multiple={true}
                accept="image/*,application/pdf"
                label={
                  input.display_name ||
                  input.name ||
                  t("stepInputsForm.uploadFilesLabel")
                }
                inputId={`file-upload-${input.id}`}
                fileNames={(fileInputValues[input.id] || []).map((f) => f.name)}
                onChange={(e) =>
                  handleFileInputChange(input.id, e.target.files)
                }
                onDelete={(fileIndex) => {
                  const updatedFiles = fileInputValues[input.id].filter(
                    (_, idx) => idx !== fileIndex
                  );
                  setFileInputValues((prev) => ({
                    ...prev,
                    [input.id]: updatedFiles,
                  }));
                  onChange(input.id, updatedFiles);
                }}
                required={required}
              />
            </Paper>
          );
        default:
          return fieldWrapper(
            <Typography color="error" variant="body2">
              {t("stepInputsForm.unsupportedInputType", {
                type: input.input_type,
              })}
            </Typography>
          );
      }
    },
    [
      inputValues,
      fileInputValues,
      handleInputChange,
      handleFileInputChange,
      handleBlur,
      touched,
      validationErrors,
      userList,
      singleSelectStyles,
      multiSelectStyles,
      getSelectOptions,
      t,
      theme,
    ]
  );

  const sortedStepInputs = useMemo(
    () => [...stepInputs].sort((a, b) => a.position - b.position),
    [stepInputs]
  );

  if (!stepInputs.length) return null;

  return (
    <Box sx={{ mt: 3 }}>
      <Typography
        variant="h6"
        gutterBottom
        sx={{
          fontWeight: 600,
          color: theme.palette.primary.main,
          mb: 3,
        }}
      >
        {t("stepInputsForm.title")}
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {sortedStepInputs.map((input, index) => renderField(input, index))}
      </Box>
    </Box>
  );
};

export default StepInputsForm;
